import { useEffect, useState } from 'react';
import { appendNoteText, fillNoteTemplate, isNoteDetailComplete, noteSlots, type NoteTemplate } from './noteTemplates.js';
import { CompletionBadge } from './CompletionBadge.js';

export function NoteTemplatePicker({ field, value, templates, disabled, onChange, onPendingChange }: {
  field: string; value: string; templates: NoteTemplate[]; disabled: boolean;
  onChange: (value: string) => void;
  onPendingChange?: (field: string, pending: boolean) => void;
}) {
  const [selected, setSelected] = useState<NoteTemplate | null>(null);
  const [details, setDetails] = useState<Record<string, string>>({});
  const [message, setMessage] = useState('');
  useEffect(() => {
    onPendingChange?.(field, Boolean(selected) && !disabled);
    return () => onPendingChange?.(field, false);
  }, [selected, disabled, field, onPendingChange]);
  const available = templates.filter((item) => item.enabled && (item.field === '*' || item.field === field))
    .sort((a, b) => Number(b.field === field) - Number(a.field === field)
      || Number(noteSlots(a.text).length > 0) - Number(noteSlots(b.text).length > 0));
  if (disabled || !available.length) return null;
  function insert(item: NoteTemplate, values: Record<string, string>) {
    try {
      onChange(appendNoteText(value, fillNoteTemplate(item.text, values)));
      setSelected(null); setDetails({}); setMessage('Wording inserted. Review and edit it to match this visit.');
    } catch (error) { setMessage(error instanceof Error ? error.message : 'The wording could not be inserted.'); }
  }
  const shortcut = (item: NoteTemplate) => <button key={item.id} type="button"
        className="btn btn-secondary !min-h-[48px] text-left whitespace-normal !justify-start"
        disabled={Boolean(selected)}
        onClick={() => {
          setMessage(''); setDetails({});
          if (noteSlots(item.text).length) setSelected(item); else insert(item, {});
        }}>{item.label}</button>;
  return <section className="mt-2 border border-neutral-300 bg-neutral-100 p-3" aria-labelledby={`note-shortcuts-${field}`}>
    <h3 id={`note-shortcuts-${field}`} className="text-base font-semibold text-accent-800">Insert standard wording</h3>
    <p className="mt-1 text-sm text-neutral-700">Choose only wording that matches this visit. Quick phrases insert with one tap; structured notes ask for details. Everything remains editable.</p>
    <div className="mt-3 grid gap-2">
      {available.slice(0, 3).map(shortcut)}
    </div>
    {available.length > 3 && <details className="mt-2">
      <summary className="min-h-[44px] cursor-pointer py-3 font-semibold text-accent-800">More wording ({available.length - 3})</summary>
      <div className="grid gap-2">{available.slice(3).map(shortcut)}</div>
    </details>}
    {selected && <div className="mt-3 space-y-3" role="group" aria-label={`Details for ${selected.label}`}>
      <p className="text-sm font-semibold">Insert or cancel these details before leaving this step.</p>
      <p className="text-sm text-neutral-700">Record only what was observed, reported, or done. State when something is unknown or not assessed. Existing notes stay intact.</p>
      <p className="text-sm whitespace-pre-wrap break-words">{selected.text}</p>
      {noteSlots(selected.text).map((slot) => <label key={slot} className="field" htmlFor={`note-${field}-${slot}`}>
        <span>{slot.replaceAll('_', ' ')} <CompletionBadge complete={isNoteDetailComplete(details[slot])} required /></span>
        <input id={`note-${field}-${slot}`} className="input" value={details[slot] ?? ''} maxLength={1000}
          autoComplete="off" aria-required="true"
          onKeyDown={(event) => { if (event.key === 'Enter' && !event.nativeEvent.isComposing) { event.preventDefault(); insert(selected, details); } }}
          onChange={(event) => setDetails({ ...details, [slot]: event.target.value })} />
      </label>)}
      <div className="flex flex-wrap gap-2">
        <button type="button" className="btn btn-primary" onClick={() => insert(selected, details)}>Insert into note</button>
        <button type="button" className="btn btn-secondary" onClick={() => { setSelected(null); setDetails({}); setMessage(''); }}>Cancel</button>
      </div>
    </div>}
    {message && <p className="mt-2 text-sm" role="status">{message}</p>}
  </section>;
}
